import React, { useCallback, useMemo } from 'react';

interface ResponsiveImageProps {
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  sizes?: string;
  priority?: boolean;
}

const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
  src,
  alt,
  className,
  width = 400,
  height = 300,
  sizes = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
  priority = false,
}) => {
  // Generate optimized image URLs using Cloudflare transformations
  const optimizedSrc = useMemo(() => {
    if (!src) return src;

    // Check if it's already a Cloudflare URL or external URL
    if (src.includes('imagedelivery.net') || src.startsWith('http')) {
      return src.includes('?')
        ? `${src}&w=${width}&h=${height}&format=webp&fit=cover`
        : `${src}?w=${width}&h=${height}&format=webp&fit=cover`;
    }
    return src;
  }, [src, width, height]);

  // Generate srcSet for responsive images
  const srcSet = useMemo(() => {
    if (!src || !src.includes('imagedelivery.net')) return undefined;

    const baseUrl = src.split('?')[0];
    return [
      `${baseUrl}?w=${Math.round(width * 0.5)}&h=${Math.round(height * 0.5)}&format=webp&fit=cover ${Math.round(width * 0.5)}w`,
      `${baseUrl}?w=${width}&h=${height}&format=webp&fit=cover ${width}w`,
      `${baseUrl}?w=${Math.round(width * 1.5)}&h=${Math.round(height * 1.5)}&format=webp&fit=cover ${Math.round(width * 1.5)}w`,
      `${baseUrl}?w=${Math.round(width * 2)}&h=${Math.round(height * 2)}&format=webp&fit=cover ${Math.round(width * 2)}w`,
    ].join(', ');
  }, [src, width, height]);

  // Optimized error handling
  const handleError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      if (img.src !== src) {
        img.src = src; // Fallback to original if optimized version fails
      }
    },
    [src],
  );

  // Calculate container aspect ratio
  const aspectRatio = useMemo(() => {
    return (height / width) * 100;
  }, [width, height]);

  return (
    <div className={`relative overflow-hidden ${className || ''}`} style={{ paddingBottom: `${aspectRatio}%` }}>
      <img
        src={optimizedSrc}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        width={width}
        height={height}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        onError={handleError}
        className="absolute inset-0 h-full w-full object-cover"
        style={{
          contentVisibility: 'auto',
          containIntrinsicSize: `${width}px ${height}px`,
        }}
      />
    </div>
  );
};

export default ResponsiveImage;
